/**
 * Turkish vocabulary, and the product rules encoded in it.
 *
 * This is not a translation table. It is the boundary where a backend enum
 * becomes a claim made to a person, and several of those claims have been
 * wrong in obvious ways before. The rules below are enforced by tests in
 * `terms.test.ts`, so breaking one fails the build rather than shipping.
 *
 * 1. **No backend string is ever displayed.** `sufficient`, `human_head`,
 *    `left_ear` are wire values. Anything that reaches a screen comes from
 *    here.
 *
 * 2. **`coverage_score` is Kapsama.** Not Doğruluk, not Olasılık, not Başarı.
 *    It measures how much of a region was captured. It says nothing about
 *    whether anything is *correct*, and a label implying accuracy would be a
 *    claim this system cannot support.
 *
 * 3. **`coverage_confidence` is Güven** - how much to trust the Kapsama figure.
 *    Two different numbers, two different words, never interchanged.
 *
 * 4. **Bilinmiyor is not a bad Yetersiz.** `unknown` means nothing ever
 *    observed the region; `insufficient` means it was observed and fell short.
 *    They get different colours, different icons and different sentences,
 *    because they call for different actions.
 *
 * 5. **Sufficient means sufficient *where measured*.** Five regions of a head
 *    are measurable. "Kafa çekimi tamamlandı" would claim the whole head; the
 *    honest sentence names the limit.
 */

import type {
  CaptureSessionStatus,
  CapturePurpose,
  CaptureType,
  CoverageRegion,
  CoverageStatus,
  GeometryMode,
  MediaAssetStatus,
  MediaType,
  ProjectStatus,
  QualityIssueSeverity,
  SubjectType,
  SurfaceProvenance,
} from "@/api/types";
import { GEOMETRY_MODES } from "@/api/types";

// --- coverage status -------------------------------------------------------

/** How a status should be treated visually. `unknown` is its own tone. */
export type StatusTone = "success" | "caution" | "danger" | "neutral";

export interface CoverageStatusTerm {
  label: string;
  tone: StatusTone;
  /** One sentence about this region's state. Never promises completeness. */
  meaning: string;
}

export const COVERAGE_STATUS_TERMS: Record<CoverageStatus, CoverageStatusTerm> = {
  sufficient: {
    label: "Yeterli",
    tone: "success",
    meaning: "Bu bölge için yeterli sayıda bağımsız görüş açısı toplandı.",
  },
  partial: {
    label: "Kısmi",
    tone: "caution",
    meaning: "Bu bölge kısmen kapsandı; ek çekim kapsamayı artırır.",
  },
  insufficient: {
    label: "Yetersiz",
    tone: "danger",
    meaning: "Bu bölge ölçüldü ancak toplanan görüntü yeterli değil.",
  },
  unknown: {
    label: "Bilinmiyor",
    tone: "neutral",
    // The distinction that matters: not measured is not the same as measured
    // and failed. This sentence must never imply a deficiency.
    meaning: "Bu bölge hiç ölçülemedi; değerlendirilebilecek bir görüntü yok.",
  },
};

export const coverageStatusLabel = (status: CoverageStatus): string =>
  COVERAGE_STATUS_TERMS[status].label;

/**
 * The overall verdict headline.
 *
 * `sufficient` deliberately says "ölçülen bölgelerde" - in the regions that
 * were measured. The system measures five regions of a head and knows nothing
 * about the back of the skull, so a headline of "Kafa çekimi tamamlandı" would
 * assert something never verified.
 */
export const OVERALL_HEADLINE: Record<CoverageStatus, string> = {
  sufficient: "Ölçülen bölgelerde kapsama yeterli",
  partial: "Ölçülen bölgelerde kapsama kısmi",
  insufficient: "Ölçülen bölgelerde kapsama yetersiz",
  unknown: "Ölçülebilen bölge bulunamadı",
};

export const OVERALL_EXPLANATION: Record<CoverageStatus, string> = {
  sufficient:
    "Ölçülen tüm bölgeler hedef kapsamaya ulaştı. Bu sonuç yalnızca aşağıda listelenen bölgeleri kapsar.",
  partial:
    "En az bir bölge hedef kapsamaya ulaşmadı. Genel sonuç her zaman en zayıf bölgeye göre belirlenir.",
  insufficient:
    "En az bir bölgede kapsama yetersiz kaldı. Genel sonuç her zaman en zayıf bölgeye göre belirlenir.",
  unknown:
    "Hiçbir bölge ölçülemedi. Bu, kapsamanın kötü olduğu anlamına gelmez; henüz değerlendirilebilecek görüntü yok.",
};

// --- the two numbers -------------------------------------------------------

/** Rule 2 and 3, as constants, so no screen can improvise a different word. */
export const METRIC_LABELS = {
  coverage: "Kapsama",
  confidence: "Güven",
  independentViews: "Bağımsız görüş",
  sourceSessions: "Kaynak çekim",
  bestSession: "En iyi çekim",
} as const;

export const METRIC_HELP = {
  coverage:
    "Bu bölgenin ne kadarının çekildiği. Doğruluk veya başarı ölçüsü değildir.",
  confidence: "Kapsama değerinin ne kadar güvenilir olduğu.",
  independentViews:
    "Birbirinden farklı açılardan alınmış görüş sayısı. Aynı açıdan çekilen çok sayıda kare tek görüş sayılır.",
  sourceSessions: "Bu sonuca katkı veren çekim oturumu sayısı.",
  bestSession: "Tek bir çekimin bu bölgede ulaştığı en yüksek kapsama.",
} as const;

// --- regions ---------------------------------------------------------------

export const COVERAGE_REGION_LABELS: Record<CoverageRegion, string> = {
  front_face: "Ön yüz",
  left_side: "Sol profil",
  right_side: "Sağ profil",
  left_ear: "Sol kulak",
  right_ear: "Sağ kulak",
};

/**
 * Areas of the head the system never measures at all.
 *
 * The API sends these as bare English codes in `unmeasured_areas`. They are
 * not a typed enum on the wire, so this table is a best-effort translation
 * with a fallback rather than an exhaustive `Record` - a new code appearing
 * must not crash the coverage screen.
 *
 * The fallback still never prints raw `snake_case` at a user. A test asserts
 * every code the backend currently produces has a real Turkish entry, so
 * drift shows up as a failing test rather than as English on the page.
 */
export const UNMEASURED_AREA_LABELS: Record<string, string> = {
  top_head: "Kafanın üstü",
  back_head: "Kafanın arkası",
  neck: "Boyun",
  under_chin: "Çene altı",
};

export function unmeasuredAreaLabel(code: string): string {
  const known = UNMEASURED_AREA_LABELS[code];
  if (known) return known;
  // Unknown code: say that an area exists without pretending to name it.
  // Dropping it silently would understate what the analysis leaves out.
  return "Adlandırılmamış bir bölge";
}

/** Turkish for the correction CTA: "Sol kulak için ek çekim gerekiyor." */
export const regionNeedsMoreCapture = (region: CoverageRegion): string =>
  `${COVERAGE_REGION_LABELS[region]} için ek çekim gerekiyor.`;

/**
 * Scope disclosure, shown wherever a coverage result is.
 *
 * The user should never have to infer what was left out. This is the
 * standing text; the actual measured/unmeasured lists come from the API.
 */
/**
 * How the overall score is arrived at.
 *
 * A real capture produced a 92% front face beside an overall 0%, which reads
 * as a contradiction until you know the rule. The backend takes the *minimum*
 * across required regions - deliberately, because an excellent front and a
 * missing ear is not a good capture and a mean would call it one. The UI's job
 * is to make that legible, not to soften it.
 */
export const OVERALL_IS_WEAKEST_REGION =
  "Genel skor, en zayıf gerekli bölgeye göre belirlenir; bölgelerin ortalaması değildir.";

/** "1 / 5 bölge yeterli" - the summary a person can act on. */
export const regionsSufficientSummary = (sufficient: number, total: number): string =>
  `${sufficient} / ${total} bölge yeterli`;

/**
 * What to do next, and it differs by status.
 *
 * UNKNOWN asks for a capture the system can measure. INSUFFICIENT asks for
 * more angles. Telling an UNKNOWN user to "capture more of the left side" when
 * they already did, and the analysis simply could not read it, is the error
 * this distinction exists to prevent.
 */
export const REGION_NEXT_ACTION: Record<CoverageStatus, string | null> = {
  sufficient: null,
  partial: "Bu bölge için birkaç farklı açıdan çekim ekleyin.",
  insufficient: "Bu bölge için daha fazla farklı açı gerekiyor.",
  unknown:
    "Bu bölge ölçülemedi. Yüzün daha net göründüğü, daha az dönük bir açıdan çekim ekleyin.",
};

/**
 * What the coverage model does and does not describe.
 *
 * The distinction this sentence has to carry: a *bölge* is one of the five the
 * model defines and can score, an *alan* is anatomy it does not model at all.
 * The second kind never becomes measurable by capturing more, which is why the
 * panel lists them under a different heading rather than as regions that are
 * merely missing.
 */
export const COVERAGE_SCOPE_NOTE =
  "Bu değerlendirme, kapsama modelinin tanımladığı bölgeleri kapsar. Kafanın arka bölümü, tepesi, boyun ve çene altı bu sürümde modelin kapsamı dışındadır; saçla kapalı alanlar ölçülemez.";

// --- subject types ---------------------------------------------------------

/**
 * The three things a user is actually trying to scan.
 *
 * A presentation layer over `subject_type`, deliberately not a new stored
 * field. The backend derives capability from `subject_type`; grouping it here
 * changes presentation without a schema migration or historical rewrite.
 *
 * **Every existing value keeps working.** A project created before this exists
 * still carries `human_head` or `room`, still opens, and now simply appears
 * under a category. Nothing is rewritten.
 *
 * The future this leaves open, and which the current code must not foreclose:
 * one capture of a *place* may eventually contain several reconstruction
 * targets - the room itself, a person in it, two objects on a table. That needs
 * a Scene/Target relationship the domain does not have, so nothing here
 * pretends it exists. What it does is stop `subject_type` from being read as
 * "this project has exactly one subject forever".
 */
export const PRODUCT_CATEGORIES = ["human", "object", "place"] as const;
export type ProductCategory = (typeof PRODUCT_CATEGORIES)[number];

export interface ProductCategoryTerm {
  label: string;
  description: string;
  /** The subject types this category offers, in the order they are shown. */
  subjectTypes: SubjectType[];
}

export const PRODUCT_CATEGORY_TERMS: Record<ProductCategory, ProductCategoryTerm> = {
  human: {
    label: "İnsan",
    description: "Bir kişiyi veya insan vücudunun belirli bir bölümünü tarayın.",
    subjectTypes: ["human_head", "human_body"],
  },
  object: {
    label: "Nesne",
    description: "Tek bir fiziksel nesneyi farklı açılardan tarayın.",
    subjectTypes: ["object"],
  },
  place: {
    label: "Mekân / Sahne",
    description: "Bir ortamı ve içindeki öğeleri çoklu görüntülerle yakalayın.",
    subjectTypes: ["room", "building", "scene", "human_in_environment"],
  },
};

/** Which category a stored subject type belongs to. Total over the enum. */
export const CATEGORY_OF_SUBJECT: Record<SubjectType, ProductCategory> = {
  human_head: "human",
  human_body: "human",
  object: "object",
  room: "place",
  building: "place",
  scene: "place",
  human_in_environment: "place",
};

export const productCategoryLabel = (category: ProductCategory): string =>
  PRODUCT_CATEGORY_TERMS[category].label;

export interface SubjectTypeTerm {
  label: string;
  /**
   * How this type reads *inside* its category, where the category name is
   * already on screen. "Baş / Yüz" under İnsan, not "İnsan kafası" under İnsan.
   */
  subtypeLabel: string;
  /**
   * Whether semantic coverage analysis exists for this subject. Only human
   * heads have it. Everything else can hold projects, sessions and media - the
   * analysis screens simply say so rather than showing an empty result that
   * looks like a failure.
   */
  hasCoverageAnalysis: boolean;
  /** Shown where analysis would be, for subjects that have none. */
  analysisNote: string;
  /** Distinct, truthful capability bullets for project creation. */
  capabilityNotes: readonly string[];
}

export const SUBJECT_TYPE_TERMS: Record<SubjectType, SubjectTypeTerm> = {
  human_head: {
    subtypeLabel: "Baş / Yüz",
    label: "İnsan kafası",
    hasCoverageAnalysis: true,
    analysisNote: "Bu konu türü için semantik kapsama analizi mevcuttur.",
    capabilityNotes: ["Fotoğraf/video çekimi", "Genel kalite analizi", "Beş bölgeli baş kapsaması", "Seyrek 3D rekonstrüksiyon"],
  },
  human_body: {
    subtypeLabel: "Tam Vücut",
    label: "İnsan vücudu",
    hasCoverageAnalysis: false,
    analysisNote: "Vücut çekiminde kalite ölçülür; semantik vücut yönleri henüz ölçülmez.",
    capabilityNotes: ["Fotoğraf/video çekimi", "Vücut çekim kalite analizi", "Canlı vücut çekim rehberliği", "Seyrek 3D rekonstrüksiyon"],
  },
  object: {
    subtypeLabel: "Nesne",
    label: "Nesne",
    hasCoverageAnalysis: false,
    // Outcome first, limit second. The sentence used to open on the semantic
    // labelling this type does not get, which answers a question nobody buying
    // a 3D scan is asking. The limit is still stated - `terms.test` requires
    // every type without coverage analysis to name what it does not measure,
    // and dropping that to sell the feature is exactly what this product does
    // not do - it just no longer arrives before the point.
    analysisNote: "Nesnenizi çevreleyen fotoğraflardan 3D model oluşturursunuz; çekim boyunca görüntü kalitesi ölçülür, semantik ön/arka yönleri atanmaz.",
    capabilityNotes: ["Fotoğraf/video çekimi", "Nesne görünüm analizi", "Canlı nesne çekim rehberliği", "Seyrek 3D rekonstrüksiyon"],
  },
  room: {
    subtypeLabel: "Oda",
    label: "Oda",
    hasCoverageAnalysis: false,
    analysisNote: "Mekânsal çekim kalitesi ölçülür; duvar/tavan gibi semantik sonuç üretilmez.",
    capabilityNotes: ["Fotoğraf/video ve telefon çekimi", "Mekânsal çekim analizi", "Mekânsal canlı rehberlik", "Seyrek 3D rekonstrüksiyon"],
  },
  building: {
    subtypeLabel: "Bina",
    label: "Bina",
    hasCoverageAnalysis: false,
    analysisNote: "Mekânsal çekim kalitesi ölçülür; cephe tamamlığı iddia edilmez.",
    capabilityNotes: ["Fotoğraf/video ve telefon çekimi", "Mekânsal çekim analizi", "Mekânsal canlı rehberlik", "Seyrek 3D rekonstrüksiyon"],
  },
  scene: {
    subtypeLabel: "Sahne",
    label: "Sahne",
    hasCoverageAnalysis: false,
    analysisNote: "Sahne çekim kalitesi ölçülür; semantik sahne tamamlığı iddia edilmez.",
    capabilityNotes: ["Fotoğraf/video ve telefon çekimi", "Mekânsal çekim analizi", "Mekânsal canlı rehberlik", "Seyrek 3D rekonstrüksiyon"],
  },
  // A place *and* the people in it. It is its own type rather than a room with
  // a note, because it carries three separate coverage truths - the place,
  // each person's face, each person's body - and refuses to average them into
  // one number. The note says exactly that, because a single percentage is the
  // thing a customer would otherwise assume.
  human_in_environment: {
    subtypeLabel: "Mekân ve İçindeki Kişiler",
    label: "Mekân ve kişiler",
    hasCoverageAnalysis: true,
    analysisNote:
      "Mekân kapsaması ile her kişinin yüz ve vücut kapsaması ayrı ayrı raporlanır; tek bir birleşik yüzde üretilmez.",
    capabilityNotes: [
      "Fotoğraf/video ve telefon çekimi",
      "Mekânsal çekim analizi",
      "Mekân kapsaması",
      "Kişi başına yüz ve vücut kapsaması",
      "Seyrek 3D rekonstrüksiyon",
    ],
  },
};

export const subjectTypeLabel = (value: SubjectType): string =>
  SUBJECT_TYPE_TERMS[value].label;

// --- geometry mode and surface provenance ----------------------------------

/**
 * The two answers to "what should happen to a surface nobody photographed?"
 *
 * The words are the customer's, not the pipeline's. "Hassas / Gerçek Geometri"
 * and "Görsel Tamamlama" carry the whole distinction, and the notes say what
 * each one *does* rather than how it does it - no mesh, no provenance, no
 * model names. Rule 1 of this file, applied to the newest enum in it.
 */
export interface GeometryModeTerm {
  label: string;
  /** One sentence, in the terms a person deciding would use. */
  note: string;
  /** What the viewer and the outputs screen say about a finished model. */
  modelNote: string;
}

export const GEOMETRY_MODE_TERMS: Record<GeometryMode, GeometryModeTerm> = {
  precise: {
    label: "Hassas / Gerçek Geometri",
    note: "Ölçüm ve 3D baskı için. Eksik alanlar yapay olarak oluşturulmaz.",
    modelNote: "Bu projede eksik alanlar yapay olarak tamamlanmaz.",
  },
  visual_completion: {
    label: "Görsel Tamamlama",
    note: "Eksik alanlar gerektiğinde yapay zekâ ile tamamlanabilir.",
    modelNote:
      "Bu projede eksik alanlar yapay zekâ ile tamamlanabilir; tamamlanan bölgeler ayrıca işaretlenir.",
  },
};

/** The choices in the order they are offered. Conservative one first. */
export const GEOMETRY_MODE_CHOICES: readonly {
  value: GeometryMode;
  label: string;
  note: string;
}[] = GEOMETRY_MODES.map((value) => ({
  value,
  label: GEOMETRY_MODE_TERMS[value].label,
  note: GEOMETRY_MODE_TERMS[value].note,
}));

export const geometryModeLabel = (value: GeometryMode | undefined): string =>
  GEOMETRY_MODE_TERMS[value ?? "precise"].label;

/**
 * What each class of surface is, said to a person.
 *
 * `unverified` is the one that needs care. It means nothing recorded where the
 * surface came from - an older model, or a stage that could not report - and
 * it is emphatically **not** a synonym for "yapay". Telling somebody their
 * pre-existing model was AI-generated would be its own false claim, which is
 * exactly why the backend keeps the two apart.
 */
export interface SurfaceProvenanceTerm {
  label: string;
  note: string;
  /** Whether a measurement touching this surface may be presented plainly. */
  measurable: boolean;
}

export const SURFACE_PROVENANCE_TERMS: Record<SurfaceProvenance, SurfaceProvenanceTerm> = {
  observed: {
    label: "Ölçülen",
    note: "Birden fazla fotoğraf bu yüzeyi gördü ve aynı sonucu verdi.",
    measurable: true,
  },
  weakly_observed: {
    label: "Zayıf ölçülen",
    note: "Bu yüzeyi tek bir fotoğraf ya da çok eğik açılar gördü. Gerçek, ama daha az desteklenmiş.",
    measurable: true,
  },
  ai_completed: {
    label: "Yapay zekâ ile tamamlanan",
    note: "Bu yüzeyi hiçbir fotoğraf görmedi; model tarafından üretildi. Ölçüm sayılmaz.",
    measurable: false,
  },
  unverified: {
    label: "Kaydı olmayan",
    note: "Bu yüzeyin nereden geldiği kaydedilmemiş. Yapay olduğu anlamına gelmez; bilinmediği anlamına gelir.",
    measurable: false,
  },
};

export const surfaceProvenanceLabel = (value: SurfaceProvenance): string =>
  SURFACE_PROVENANCE_TERMS[value].label;

// --- projects and sessions -------------------------------------------------

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  draft: "Taslak",
  active: "Aktif",
  archived: "Arşivlenmiş",
};

export const PROJECT_STATUS_TONES: Record<ProjectStatus, StatusTone> = {
  draft: "neutral",
  active: "success",
  archived: "neutral",
};

export const CAPTURE_TYPE_LABELS: Record<CaptureType, string> = {
  photo: "Fotoğraf",
  video: "Video",
  mixed: "Karışık",
};

export const CAPTURE_PURPOSE_LABELS: Record<CapturePurpose, string> = {
  initial: "İlk çekim",
  correction: "Düzeltme çekimi",
};

/**
 * The capture lifecycle, which is about *input* and not about analysis.
 *
 * `complete` used to be unreachable - every session was created `pending` and
 * nothing moved it - so a fully analysed capture still read "Beklemede", which
 * was true of the data and useless to the reader. It is now reached by an
 * explicit finalize action, because nothing can tell whether someone intends
 * to upload one more photograph.
 *
 * "Sonlandırılmadı" rather than "Beklemede": nothing is waiting on the system,
 * the capture is simply still open.
 */
export const CAPTURE_SESSION_STATUS_LABELS: Record<CaptureSessionStatus, string> = {
  pending: "Sonlandırılmadı",
  complete: "Sonlandırıldı",
  failed: "Başarısız",
};

/** Shown beside the badge so the state implies an action, not a wait. */
export const CAPTURE_SESSION_STATUS_MEANING: Record<CaptureSessionStatus, string> = {
  pending:
    "Çekim henüz sonlandırılmadı. Dosya eklemeyi bitirdiğinizde sonlandırabilirsiniz; analizler daha sonra da çalıştırılabilir.",
  complete:
    "Çekim girdisi sonlandırıldı. Analizler bundan sonra da yeniden çalıştırılabilir.",
  failed: "Bu çekim işlenemedi.",
};

export const CAPTURE_SESSION_STATUS_TONES: Record<CaptureSessionStatus, StatusTone> = {
  pending: "neutral",
  complete: "success",
  failed: "danger",
};

// --- media -----------------------------------------------------------------

export const MEDIA_TYPE_LABELS: Record<MediaType, string> = {
  photo: "Fotoğraf",
  video: "Video",
};

export const MEDIA_STATUS_LABELS: Record<MediaAssetStatus, string> = {
  uploaded: "Yüklendi",
  probing: "İnceleniyor",
  ready: "Hazır",
  failed: "Başarısız",
};

export const MEDIA_STATUS_TONES: Record<MediaAssetStatus, StatusTone> = {
  uploaded: "neutral",
  probing: "caution",
  ready: "success",
  failed: "danger",
};

// --- issues ----------------------------------------------------------------

export const SEVERITY_LABELS: Record<QualityIssueSeverity, string> = {
  info: "Bilgi",
  warning: "Uyarı",
  critical: "Kritik",
};

export const SEVERITY_TONES: Record<QualityIssueSeverity, StatusTone> = {
  info: "neutral",
  warning: "caution",
  critical: "danger",
};

// --- errors ----------------------------------------------------------------

/**
 * Turkish sentences for each failure kind.
 *
 * Chosen by kind, never taken from the response. A user should learn what to
 * do next; a stack trace teaches them nothing and exposes internals.
 */
export const ERROR_MESSAGES = {
  network: "Sunucuya ulaşılamadı. Bağlantınızı kontrol edip tekrar deneyin.",
  canceled: "İşlem iptal edildi.",
  timeout: "İşlem zaman aşımına uğradı. Sunucu hâlâ çalışıyor olabilir.",
  validation: "Gönderilen bilgiler kabul edilmedi. Alanları kontrol edin.",
  not_found: "Aradığınız kayıt bulunamadı.",
  conflict: "Bu işlem mevcut durumla çakışıyor. Sayfayı yenileyip tekrar deneyin.",
  too_large: "Dosya sunucunun kabul ettiği boyuttan büyük.",
  unsupported_media: "Bu dosya türü desteklenmiyor.",
  unavailable: "Gerekli bir servis şu anda kullanılamıyor. Daha sonra tekrar deneyin.",
  server: "Sunucuda beklenmeyen bir hata oluştu.",
  malformed: "Sunucudan beklenmeyen bir yanıt alındı.",
  unknown: "Beklenmeyen bir hata oluştu.",
} as const;

// --- honest absences -------------------------------------------------------
// Sentences for the things the product does not do yet. Each names the gap
// instead of hiding it behind a disabled button with no explanation.

export const NOT_YET = {
  // No longer "there is no camera": the phone has one. What this computer
  // does not have is a camera of its own, and the sentence now says which is
  // which instead of denying a feature that exists two metres away.
  liveCamera:
    "Bu bilgisayarda canlı kamera yok. Telefonunuzu QR ile bağlayıp fotoğraf çekebilir veya dosyalarınızı buraya yükleyebilirsiniz.",
  reconstruction:
    "3B yeniden yapılandırma bu sürümde yok. Şu anda yalnızca çekim kapsaması değerlendiriliyor.",
  mediaPreview:
    "Önizleme yok: sunucu şu anda medya dosyalarını servis etmiyor. Yalnızca dosya bilgileri gösteriliyor.",
  orchestration:
    "Analiz adımları tek tek çalıştırılır. Sunucuda hepsini birden yürüten bir uç nokta yok.",
} as const;

/**
 * Phone pairing, in product language.
 *
 * The wording is deliberately about *connection*, never about live capture or
 * AI guidance: the phone can pair and nothing more, and a label promising
 * "Canlı AI Çekim" would be describing a feature that does not exist. When the
 * camera phase lands, these strings change - not before.
 */
export const PAIRING_TERMS = {
  sectionTitle: "Telefon ile Çekim",
  sectionBody:
    "Telefonunuzu SentyCap'e bağlayarak çekimleri doğrudan bu projeye aktarabilirsiniz.",
  connectButton: "Telefon Bağla",
  dialogTitle: "Telefonu Bağla",
  scanInstruction: "Telefonunuzun kamerasıyla QR kodu tarayın.",
  expiryNotice: "Bağlantı kodu kısa süre içinde sona erer.",
  shareWarning: "Bu kodu yalnızca bağlamak istediğiniz telefonla paylaşın.",
  waiting: "Telefon bekleniyor…",
  connected: "Telefon bağlandı",
  deviceLabel: "Cihaz",
  connectionLabel: "Bağlantı",
  connectionReady: "Hazır",
  disconnect: "Bağlantıyı Kes",
  newCode: "Yeni Kod Oluştur",
  cancel: "İptal",
  retry: "Tekrar Dene",
  expired: "Bağlantı kodunun süresi doldu.",
  revoked: "Bağlantı iptal edildi.",
  failed: "Telefon bağlantısı kurulamadı.",
  sameNetwork: "Telefon ve SentyCap aynı yerel ağda olmalıdır.",
  notConfiguredTitle: "Telefon bağlantısı yapılandırılmadı.",
  notConfiguredAmbiguous:
    "Bu bilgisayarda birden fazla ağ adresi var. Hangisinin kullanılacağı sunucu ayarlarında belirtilmelidir.",
  notConfiguredNoInterface:
    "Bu bilgisayarda telefonun ulaşabileceği bir yerel ağ adresi bulunamadı.",
  notConfiguredDisabled: "Telefon bağlantısı bu sunucuda kapalı.",
  /**
   * The one condition on phone capture, stated as a condition.
   *
   * This used to read "canlı kamera çekimi bir sonraki aşamada
   * etkinleştirilecek", which was true when pairing shipped and stopped being
   * true when the phone capture page shipped. Browsers only expose a camera in
   * a secure context, so the accurate sentence is conditional on the address,
   * not on the release - over HTTPS the camera works today, and over a plain
   * LAN address it never will.
   */
  cameraRequiresHttps:
    "Telefon bağlantısı hazır. Kamera yalnızca güvenli (HTTPS) bir adreste açılabilir; yerel ağ adresiyle bağlandıysanız telefon bunu bildirir ve dosyalarınızı bilgisayardan yükleyebilirsiniz.",
} as const;

/**
 * Reconstruction, in product language.
 *
 * `denseUnavailable` is the sentence that replaces the mockup's "Bekliyor" on
 * the dense, mesh and texture stages. Nothing is waiting: the available COLMAP
 * build has no CUDA, so those stages have no execution path at all on this
 * worker, and telling the user to wait would be telling them to wait forever.
 */
export const RECONSTRUCTION_TERMS = {
  status: {
    queued: "Sırada",
    running: "İşleniyor",
    // Not "Seyrek 3D hazır". That was true when sparse was the only thing this
    // pipeline could finish with, and became a lie the day a job could end
    // with a surface model. What a finished job actually produced is in
    // `model_state`, which is the one place allowed to answer it.
    succeeded: "Tamamlandı",
    failed: "Başarısız",
    cancelled: "İptal edildi",
  } as Record<string, string>,
  denseUnavailable:
    "Yoğun 3D, mesh ve doku bu sunucudaki rekonstrüksiyon motorunda çalıştırılamıyor. Bu aşamalar sırada beklemiyor.",
  noModel: "Henüz nihai 3D model oluşturulmadı.",
  sparseOnly: "Mevcut en ileri çıktı: Seyrek 3D",
  scaleUncalibrated: "Kalibre edilmemiş",
  scaleVerified: "Doğrulandı",
  /**
   * What to say about scale, and it depends on whether there is one.
   *
   * It used to be one sentence saying the model is never calibrated and that
   * this version measures nothing in millimetres. Both stopped being true when
   * scale calibration and the measurement panel shipped, and the screen went
   * on saying them - to customers who had just calibrated their own model. A
   * product that denies a capability it has is the same class of defect as one
   * that invents one.
   *
   * `verified` still says what a scale is *not*: a calibrated model is not a
   * printable one. Those two fail independently and the product reports them
   * independently.
   */
  scaleNote: (verified: boolean): string =>
    verified
      ? "Bu modelin ölçeği bir referans uzunlukla doğrulandı; ölçümler gerçek birimlerde gösterilir. Ölçek doğrulanmış olması, modelin baskıya hazır olduğu anlamına gelmez."
      : "Ölçek kalibre edilmemiştir; model gerçek dünya ölçüsünde değildir. Ölçüm panelinden bilinen bir uzunluk girerek ölçeği belirleyebilirsiniz.",

  /**
   * What a running job is doing, in the words somebody waiting would use.
   *
   * Every one of these is a phase the worker actually reported. There is no
   * entry for a state the pipeline cannot be in, and no percentage: the engine
   * publishes none, and a number invented here would move at a speed that
   * means nothing.
   */
  phase: {
    input_transfer: "Girdiler aktarılıyor",
    sparse: "Seyrek 3D çıkarılıyor",
    dense: "Yoğun 3D çıkarılıyor",
    mesh: "Yüzey oluşturuluyor",
    glb: "Model hazırlanıyor",
    output_transfer: "Sonuç aktarılıyor",
  } as Record<string, string>,

  /** A running job whose worker has not reported a phase yet. */
  phaseUnknown: "Başlatılıyor",

  /**
   * The two things a customer starts, named by what they get.
   *
   * `Seyrek`, `Dense`, `Mesh` and `GLB` are the pipeline's words for its own
   * stages. They belong under "Teknik ayrıntılar"; the buttons say what the
   * run is for.
   */
  quickCheckAction: "Hızlı 3D Kontrol",
  quickCheckNote:
    "Fotoğraflarınızın birbiriyle eşleşip eşleşmediğini ve kamera konumlarını çıkarır. Yüzey modeli üretmez; genellikle birkaç dakika sürer.",
  surfaceAction: "Yüzey Modeli Oluştur",
  surfaceNote:
    "Görüntülenebilir bir 3D yüzey modeli üretir. Bu işlem, hızlı kontrole göre belirgin şekilde daha uzun sürer.",

  /**
   * The surface feature when no machine that can run it is connected.
   *
   * Shown rather than removed. The controls used to disappear entirely, which
   * left the reader asking where their buttons had gone with nothing on screen
   * to answer them. No worker name, no heartbeat vocabulary: the customer does
   * not have a fleet.
   */
  surfaceUnavailable: "Şu anda kullanılamıyor",
  surfaceUnavailableNote:
    "3D işleme sunucusu şu anda bağlı değil. Bağlantı geri geldiğinde bu seçenek kendiliğinden kullanılabilir olur; bir şey yapmanız gerekmez.",

  /**
   * Connected, and unable to do this particular thing.
   *
   * The sentence above was being shown for this case, and it was false: the
   * server was connected, had answered, and had just finished a sparse
   * reconstruction. Telling a customer to wait for a connection they already
   * have makes the product look broken and makes them wait for nothing.
   *
   * No "CUDA", no "COLMAP", no "nocuda" - the customer has no fleet and no
   * build to inspect. "GPU desteği" is as far into the machine as this goes,
   * and it is the part that decides what they can do next: this is ours to
   * fix, not theirs, and their photographs are fine.
   */
  surfaceNoGpu: "Bu sunucuda yüzey modeli üretilemiyor",
  surfaceNoGpuNote:
    "3D işleme sunucusu bağlı, ancak bu sunucuda GPU desteği bulunmadığı için yüzey modeli oluşturulamıyor. Hızlı 3D kontrolü çalıştırabilirsiniz. Çekiminizde bir sorun yok.",

  surfaceUnknown: "Durum kontrol ediliyor",
  surfaceUnknownNote:
    "3D işleme sunucusunun durumu henüz okunamadı. Bu, kullanılamadığı anlamına gelmez.",

  /**
   * Starting again, named for what it does.
   *
   * A re-run creates a *new* version beside the old one - it does not replace
   * a stored result - and "Yeniden Çalıştır" reads as though it might.
   */
  rerunAction: "Yeni bir 3D çalışma başlat",
  rerunNote:
    "Önceki çalışma ve çıktıları olduğu gibi kalır; bu, geçmişe eklenen yeni bir sürümdür.",

  /** A run that is genuinely behind other work. */
  queuedBehindWorkNote:
    "İş sırada. Önceki işlem tamamlandığında kendiliğinden başlayacak.",

  /**
   * Queued, and the difference between the two reasons for it.
   *
   * A job waiting because the one GPU is busy is a queue; a job waiting
   * because no capable machine is connected is a different fact and the
   * customer can tell, so the product says which.
   */
  queuedBehindWork: "Sırada",
  queuedNoWorker: "GPU işleyicisi bekleniyor",
  queuedNoWorkerNote:
    "Bu proje yüzey modeli üretebilen bir işleyici bağlandığında otomatik olarak işlenecek. Sayfayı açık tutmanıza gerek yok.",

  /** The reconstruction keeps running with the browser closed. */
  runsInBackground:
    "İşlem sunucuda sürüyor. Sayfayı kapatabilirsiniz; döndüğünüzde kaldığı yerden görürsünüz.",
} as const;

/**
 * Naming a piece of running work for the deletion dialog.
 *
 * The backend sends a stable `kind` and the coarse `phase` a worker last
 * reported; the sentence is built here, because nothing user-facing is stored
 * in a language and because the same run must not read two ways on two
 * screens.
 *
 * The phase words are the customer-facing ones the reconstruction screen
 * already uses - "Kamera konumları belirleniyor", not "sparse". A dialog that
 * says `sparse` has stopped being a confirmation and started being a log.
 *
 * A queued job says it is queued rather than naming a phase, because it has
 * not started one; a running job with no phase reported yet says the general
 * true thing rather than guessing at a stage.
 */
const ACTIVE_WORK_PHASES: Record<string, string> = {
  input_transfer: "Fotoğraflar hazırlanıyor",
  sparse: "Kamera konumları belirleniyor",
  dense: "3D yapı ayrıntılandırılıyor",
  mesh: "Yüzey oluşturuluyor",
  glb: "Yüzey oluşturuluyor",
  texture: "Doku hazırlanıyor",
  output_transfer: "Sonuç kaydediliyor",
};

export function activeWorkLabel(work: {
  kind: string;
  status: string;
  phase: string | null;
}): string {
  if (work.kind !== "reconstruction") {
    // Nothing else is asynchronous today, and inventing a sentence for a kind
    // this build does not know would be a guess on a confirmation screen.
    return "Devam eden bir işlem";
  }
  if (work.status === "queued") return "3D model oluşturma (sırada)";
  const phase = work.phase ? ACTIVE_WORK_PHASES[work.phase] : undefined;
  return phase ? `3D model oluşturuluyor · ${phase}` : "3D model oluşturuluyor";
}

/**
 * Why a reconstruction failed, for the person who ran it.
 *
 * Two rules decide the wording. A failure the customer can act on says what to
 * do; a failure of our own infrastructure says so plainly and does not send
 * somebody out to reshoot a capture that was fine. And nothing here names a
 * stage, a binary, a host or an enum - those belong in a log.
 */
export const RECONSTRUCTION_FAILURE_TERMS: Record<string, string> = {
  not_enough_input:
    "Bu çekimde rekonstrüksiyon için yeterli kullanılabilir kare yok. Daha fazla fotoğraf ekleyip tekrar deneyin.",
  insufficient_input:
    "Bu çekimde rekonstrüksiyon için yeterli kullanılabilir kare yok. Daha fazla fotoğraf ekleyip tekrar deneyin.",
  input_transfer_failed:
    "Girdiler işleyiciye aktarılamadı. Bu bir sunucu sorunudur; çekiminizde bir sorun yok. Tekrar çalıştırabilirsiniz.",
  feature_extraction_failed:
    "Fotoğraflarda eşleştirilebilir yeterli detay bulunamadı. Daha aydınlık, daha net ve dokusu belirgin yüzeyler içeren kareler yardımcı olur.",
  feature_matching_failed:
    "Fotoğraflar birbiriyle eşleştirilemedi. Kareler arasında daha fazla örtüşme gerekir; nesnenin etrafında daha küçük adımlarla dönerek çekin.",
  sparse_reconstruction_failed:
    "Kameraların konumu çözülemedi. Genellikle sebebi kareler arası örtüşmenin azlığı veya hareket bulanıklığıdır.",
  // "bağlı değil" was wrong here for the same reason it was wrong on the
  // surface controls: production had a connected worker that simply could not
  // run this stage. "bulunamadı" is true whether the machine is absent or
  // present-and-unable, and neither sends the customer out to reshoot.
  dense_unavailable:
    "Yoğun 3D aşamasını çalıştırabilecek bir işleyici bulunamadı. Bu bir altyapı durumudur; çekiminizde bir sorun yok.",
  dense_reconstruction_failed:
    "Yoğun 3D aşaması tamamlanamadı. Çekimde daha fazla açı ve örtüşme sonucu iyileştirir.",
  mesh_unavailable:
    "Yüzey oluşturabilecek bir işleyici bulunamadı. Bu bir altyapı durumudur; çekiminizde bir sorun yok.",
  mesh_generation_failed:
    "Yüzey oluşturulamadı. Yoğun 3D verisi bir yüzey çıkarmaya yetecek yoğunlukta değil.",
  glb_packaging_failed:
    "Model dosyası hazırlanamadı. Bu bir sunucu sorunudur; tekrar çalıştırabilirsiniz.",
  artifact_validation_failed:
    "Üretilen geometri doğrulamayı geçemedi. Sonuç teslim edilmedi.",
  output_transfer_failed:
    "Sonuç sunucuya aktarılamadı. Bu bir sunucu sorunudur; tekrar çalıştırabilirsiniz.",
  engine_unavailable:
    "Rekonstrüksiyon motoru şu anda kullanılamıyor. Bu bir altyapı durumudur; çekiminizde bir sorun yok.",
  engine_timeout:
    "İşlem ayrılan süreyi aştı. Daha az fotoğrafla tekrar deneyebilirsiniz.",
  engine_error:
    "Rekonstrüksiyon beklenmedik bir hatayla durdu. Tekrar çalıştırabilirsiniz.",
};

/** The failure sentence for a code, or a safe one when the code is unknown. */
export function reconstructionFailureMessage(code: string | null | undefined): string {
  // An unrecognised code is a code this build has not been taught yet, which
  // is not the customer's problem and must not surface as an enum name.
  //
  // The fallback used to offer to put them in touch with support. There is no
  // support channel in this product, and a sentence that sends somebody
  // looking for one is worse than admitting the failure is unexplained.
  return (
    (code && RECONSTRUCTION_FAILURE_TERMS[code]) ||
    "3D oluşturma tamamlanamadı. Nedeni belirlenemedi."
  );
}

/** `12 fotoğraf`, or a dash when there is nothing to count. */
export const formatCount = (value: number | null | undefined, unit: string): string =>
  value === null || value === undefined ? "—" : `${value} ${unit}`;

/** Turkish decimals, or a dash. Never a fabricated zero. */
export const formatDecimal = (
  value: number | null | undefined,
  suffix = "",
  digits = 2,
): string =>
  value === null || value === undefined
    ? "—"
    : `${value.toFixed(digits).replace(".", ",")}${suffix}`;
